import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import type { Partner } from "./usePartners";
import type { PartnerCode } from "./usePartnerCodes";

type CreatePartnerInput = Omit<Partner, "id" | "created_at" | "updated_at">;
type UpdatePartnerInput = Partial<Omit<Partner, "id" | "created_at" | "updated_at">> & { id: string };
type CreateCodeInput = Omit<PartnerCode, "id" | "created_at">;
type UpdateCodeInput = Partial<Omit<PartnerCode, "id" | "created_at" | "partner_id">> & { id: string; partner_id: string };

export function usePartnerMutations() {
  const qc = useQueryClient();

  const createPartner = useMutation({
    mutationFn: async (input: CreatePartnerInput) => {
      const { data, error } = await supabase
        .from("partners")
        .insert({ ...input, updated_at: new Date().toISOString() })
        .select()
        .single();
      if (error) throw error;
      return data as Partner;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partners"] });
      toast.success("Partner created.");
    },
    onError: (e) => toast.error("Failed to create partner: " + (e as Error).message),
  });

  const updatePartner = useMutation({
    mutationFn: async ({ id, ...fields }: UpdatePartnerInput) => {
      const { error } = await supabase
        .from("partners")
        .update({ ...fields, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partners"] });
      toast.success("Partner saved.");
    },
    onError: (e) => toast.error("Failed to save partner: " + (e as Error).message),
  });

  const createCode = useMutation({
    mutationFn: async (input: CreateCodeInput) => {
      const { data, error } = await supabase
        .from("partner_referral_codes")
        .insert(input)
        .select()
        .single();
      if (error) throw error;
      return data as PartnerCode;
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ["partner-codes", vars.partner_id] });
      toast.success("Referral code created.");
    },
    onError: (e) => toast.error("Failed to create code: " + (e as Error).message),
  });

  const updateCode = useMutation({
    mutationFn: async ({ id, partner_id, ...fields }: UpdateCodeInput) => {
      const { error } = await supabase
        .from("partner_referral_codes")
        .update(fields)
        .eq("id", id);
      if (error) throw error;
      return { id, partner_id };
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ["partner-codes", result.partner_id] });
      toast.success("Code updated.");
    },
    onError: (e) => toast.error("Failed to update code: " + (e as Error).message),
  });

  return { createPartner, updatePartner, createCode, updateCode };
}
